import { LinkArrow } from "assets/icons";
import { t } from "i18next";
import { GoesWithStrip } from "modules/recommendation";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Album, Button } from "ui";

import {
  ColorsSelector,
  NameAndPricesBlock,
  ProductDetailsList,
  ProductInfoBlock,
  SizesList
} from "./atoms";
import { ProductInfoContentProps } from "./ProductInfoContent.types";

import styles from "./ProductInfoContent.module.scss";

const ProductInfoContent = ({ product }: ProductInfoContentProps) => {
  const { productVariantId } = useParams();
  const activeProductVariant =
    product.variants.find(
      (productVariant) => productVariant.id === productVariantId
    ) ?? product.variants[0];

  const [selectedSizeValueId, setSelectedSizeValueId] = useState(
    activeProductVariant.barcodes[0]?.sizeAttributeValueId
  );

  const mediaList = useMemo(() => {
    return [
      ...(activeProductVariant.preview ? [activeProductVariant.preview] : []),
      ...activeProductVariant.images,
      ...(product.generalVideo ? [product.generalVideo] : [])
    ];
  }, [activeProductVariant, product]);

  useEffect(() => {
    setSelectedSizeValueId(
      activeProductVariant.barcodes.find(
        (barcode) => barcode.sizeAttributeValueId === selectedSizeValueId
      )?.sizeAttributeValueId ??
        activeProductVariant.barcodes[0]?.sizeAttributeValueId
    );
  }, [activeProductVariant]);

  return (
    <div className={styles.productInfo}>
      <div className={styles.productInfo_mainInfoContainer}>
        <div className={styles.productInfo_mainInfoBlock}>
          <Album
            mediaList={mediaList}
            className={styles.productInfo_album}
            extraActions={
              product.googleDriveLink ? (
                <Button
                  variant="default"
                  rightIcon={<LinkArrow width={16} height={16} />}
                  onClick={() =>
                    window.open(
                      product.googleDriveLink as string,
                      "_blank",
                      "noopener,noreferrer"
                    )
                  }
                >
                  {t("Google Drive")}
                </Button>
              ) : null
            }
          />

          <ProductInfoBlock title={t("Description")}>
            <p
              className={styles.productInfo_desccription}
              data-testid="productInfo--description"
            >
              {product.description}
            </p>
          </ProductInfoBlock>
        </div>

        <div className={styles.productInfo_mainInfoBlock}>
          <NameAndPricesBlock activeProductVariant={activeProductVariant} />

          <div className={styles.productInfo_separatorLine} />

          <ProductInfoBlock title={t("Colors")}>
            <ColorsSelector variantsList={product.variants} />
          </ProductInfoBlock>

          <div className={styles.productInfo_separatorLine} />

          <ProductInfoBlock title={t("Sizes")}>
            <SizesList
              barcodes={activeProductVariant.barcodes}
              selectedSizeValueId={selectedSizeValueId}
              onSizeButtonClick={setSelectedSizeValueId}
            />
          </ProductInfoBlock>

          <div className={styles.productInfo_separatorLine} />

          <ProductInfoBlock title={t("Product details")}>
            <ProductDetailsList
              product={product}
              activeProductVariant={activeProductVariant}
              selectedSizeValueId={selectedSizeValueId}
            />
          </ProductInfoBlock>
        </div>
      </div>

      <div className={styles.productInfo_goesWith}>
        <div className={styles.productInfo_separatorLine} />
        <ProductInfoBlock title={t("Goes with this product")}>
          <GoesWithStrip productId={product.id} />
        </ProductInfoBlock>
      </div>
    </div>
  );
};

export default ProductInfoContent;
